<template>
  <template v-if="populatedForm?.fields">
    <div class="mpf-grid-row">
      <div v-if="shouldShowNhsLoginButtons" class="mpf-grid-column-two-thirds">
        <h2 class="mpf-heading-l">Provide your details</h2>
        <p class="mpf-u-margin-bottom-6">
          We use your details to match your request to your patient record.
        </p>
        <mpf-button
          :label="personalDetailsButtonLabel"
          variant="login"
          @click="showPatientDetailsForm = true"
          class="mpf-button--auto-width"
        />
        <div class="hr-with-text mpf-u-margin-0 mpf-u-margin-bottom-4">
          <span class="hr-text">or</span>
        </div>
        <button
          class="nhslogin-button nhslogin-button-logo mpf-button--auto-width"
          @click="openNhsLogin()"
        >
          <div class="button-logo">
            <svg
              class="nhsuk-logo"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 40 16"
              height="27"
              width="67"
            >
              <path class="nhsuk-logo__background" fill="#fff" d="M0 0h40v16H0z"></path>
              <path
                class="nhsuk-logo__text"
                fill="#005eb8"
                d="M3.9 1.5h4.4l2.6 9h.1l1.8-9h3.3l-2.8 13H9l-2.7-9h-.1l-1.8 9H1.1M17.3 1.5h3.6l-1 4.9h4L25 1.5h3.5l-2.7 13h-3.5l1.1-5.6h-4.1l-1.2 5.6h-3.4M37.7 4.4c-.7-.3-1.6-.6-2.9-.6-1.4 0-2.5.2-2.5 1.3 0 1.8 5.1 1.2 5.1 5.1 0 3.6-3.3 4.5-6.4 4.5-1.3 0-2.9-.3-4-.7l.8-2.7c.7.4 2.1.7 3.2.7s2.8-.2 2.8-1.5c0-2.1-5.1-1.3-5.1-5 0-3.4 2.9-4.4 5.8-4.4 1.6 0 3.1.2 4 .6"
              ></path>
            </svg>
          </div>
          Continue to NHS login
        </button>
      </div>
      <mpf-form
        v-if="shouldShowPatientDetailsForm"
        :form-title="populatedForm.config?.formTitle"
        :hint="populatedForm.config?.hint"
        :fields="populatedForm?.fields"
        @submit="(e) => handleSubmit(e)"
        submit-label="Continue"
        class="mpf-grid-column-full"
      />
    </div>
  </template>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useRouteMeta } from '../composables/useRouteMeta';
  import MpfForm from '../components/MpfForm/MpfForm.vue';
  import { useMedicalRequestStore } from '../../stores/useMedicalRequestStore';
  import { useMergeDataIntoForm } from '../composables/useMergeDataIntoForm';
  import { formPatientDetailsProxy, formPatientDetailsYou } from '../../data/forms';
  import { MpfFormPropsWithConfig } from '../../types/router-meta';

  import { useUrlSegments } from '../composables/useUrlSegments';
  import { useAdminRequestStore } from '../../stores/useAdminRequestStore';
  import { useRepeatPrescriptionRequestStore } from '../../stores/useRepeatPrescriptionRequestStore';
  import {
    RequestType,
    RequestTypeShort,
    VerifyPatientResult,
  } from '../../types';
  import MpfButton from '../components/MpfButton/MpfButton.vue';
  import { useTenantStore } from '../../stores/useTenantStore';
  import { storeToRefs } from 'pinia';
  import { useNhsLogin } from '../composables/useNhsLogin';

  import { useRouter } from 'vue-router';
  import { useRequestFormAvailability } from '../composables/useRequestFormAvailability';
  import { useSessionStore } from '../../stores/useSessionStore';
  import { useProvidedSessionInactivity } from '../composables/useProvidedSessionInactivity';
  const { getPortalLink } = useNhsLogin();

  const { formConfig } = useRouteMeta();
  const { firstSegment } = useUrlSegments();
  const { mergeDataIntoForm } = useMergeDataIntoForm();
  const { check } = useRequestFormAvailability();

  const tenantStore = useTenantStore();
  const { isNhsJurisdiction, isNhsLoginEnabled } = storeToRefs(tenantStore);

  const medicalRequestStore = useMedicalRequestStore();
  const adminRequestStore = useAdminRequestStore();
  const repeatPrescriptionRequestStore = useRepeatPrescriptionRequestStore();

  const showPatientDetailsForm = ref<boolean>(false);

  // NB. session countdown starts
  const session = useSessionStore();
  const inactivity = useProvidedSessionInactivity();

  const openNhsLogin = async () => {
    const requestType = firstSegment.value.replace('-request', '') as RequestTypeShort;
    const url = await getPortalLink(requestType);
    if (!url) return;
    window.location.assign(url);
  };

  let store: typeof medicalRequestStore | typeof adminRequestStore;

  // TODO. create store definer script or move shared fields to common store
  if (firstSegment.value === 'medical-request') {
    store = medicalRequestStore;
  } else if (firstSegment.value === 'admin-request') {
    store = adminRequestStore;
  }

  const personalDetailsButtonLabel = computed(() =>
    store?.reporterType === 'PROXY' ? 'Enter patient details' : 'Enter my personal details',
  );

  const shouldShowNhsLoginButtons = computed(() => {
    const s = store;
    if (!isNhsLoginEnabled.value) {
      return false;
    }
    if (!isNhsJurisdiction.value) {
      return false;
    }
    if (!showPatientDetailsForm.value) return s?.reporterType !== 'PROXY';
  });

  const shouldShowPatientDetailsForm = computed(() => {
    return (
      showPatientDetailsForm.value ||
      store?.reporterType === 'PROXY' ||
      (!isNhsJurisdiction.value && store?.reporterType === 'PATIENT') ||
      store?.reporterType === null ||
      (!isNhsJurisdiction.value && firstSegment.value === 'repeat-prescription-request') ||
      !isNhsLoginEnabled.value
    );
  });

  const router = useRouter();

  const handleSubmit = async (data: any) => {
    if (!data.valid) return;

    let store:
      | typeof medicalRequestStore
      | typeof adminRequestStore
      | typeof repeatPrescriptionRequestStore;

    const formStatus = await check(firstSegment.value as RequestType);
    if (!formStatus) return;

    // NB, prescription need verification step
    // slightly different logic for prescriptions
    let routeChild: string = 'medical-warning';

    if (firstSegment.value === 'medical-request') {
      store = medicalRequestStore;
    } else if (firstSegment.value === 'admin-request') {
      store = adminRequestStore;
      routeChild = 'medical-warning';
    } else if (firstSegment.value === 'repeat-prescription-request') {
      store = repeatPrescriptionRequestStore;
      // NB. starts new here as can't use proxy
      store.startNew();
      routeChild = 'verification-code';
    } else {
      throw new Error(`Unsupported first segment`);
    }

    const result: VerifyPatientResult = await store.verifyPatientDetails(data.values);
    if (result?.ok) {
      session.startSession(Date.now());
      inactivity.start();
      await router.push(`/${firstSegment.value}/${routeChild}`);
    }
  };

  // load me or someone else form
  const meOrSomeoneElseForm = computed(() =>
    !store?.reporterType || store?.reporterType === 'PATIENT'
      ? formPatientDetailsYou
      : formPatientDetailsProxy,
  );

  const populatedForm = computed(() => {
    const config = formConfig.value as MpfFormPropsWithConfig | undefined;

    if (!config) return null;
    return (
      mergeDataIntoForm(meOrSomeoneElseForm.value, firstSegment.value, 'patientDetails') ?? config
    );
  });
</script>
